import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Task } from 'ember-concurrency';
import { all, dropTask, restartableTask } from 'ember-concurrency';
import type AgreementModel from 'garaje/models/agreement';
import type AgreementPageModel from 'garaje/models/agreement-page';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import type TrialService from 'garaje/services/trial';

interface FlowsSectionsLegalDocumentsListComponentSignature {
  Args: {
    agreementPage: AgreementPageModel;
    agreements: AgreementModel[];
    updateAndSaveTask: Task<void, [AgreementModel, string, boolean, boolean]>;
    trackFeatureToggled: (value: boolean) => void;
  };
}

export default class FlowsSectionsLegalDocumentsListComponent extends Component<FlowsSectionsLegalDocumentsListComponentSignature> {
  @service declare trial: TrialService;
  @service declare state: StateService;
  @service declare metrics: MetricsService;
  @service declare flashMessages: FlashMessagesService;

  @tracked agreementPendingDelete: AgreementModel | null = null;

  get isShowingDeleteConfirmation(): boolean {
    return !!this.agreementPendingDelete;
  }

  get legalDocumentsEnabled(): boolean {
    return this.args.agreementPage.enabled && !!this.state.features?.includesNda;
  }

  get hasAccessToIncludesNdaFeature(): boolean {
    return Boolean(this.state.features?.includesNda);
  }

  updateAgreementPositions = restartableTask(async (agreements: AgreementModel[]) => {
    await all(
      agreements.map((agreement, index) => {
        agreement.position = index + 1;
        // @ts-ignore custom save option
        return agreement.save({ propagable: false });
      }),
    );
  });

  @action
  toggleAgreementEnabled(agreement: AgreementModel, value: boolean): void {
    void this.args.updateAndSaveTask.perform(agreement, 'enabled', value, false);
  }

  @action
  toggleAgreementPageEnabled(agreementPage: AgreementModel, value: boolean): void {
    void this.args.updateAndSaveTask.perform(agreementPage, 'enabled', value, false);
    this.args.trackFeatureToggled(value);
  }

  @action
  confirmDeleteAgreement(agreement: AgreementModel): void {
    this.agreementPendingDelete = agreement;
  }

  @action
  cancelDelete(): void {
    this.agreementPendingDelete = null;
  }

  deleteAgreement = dropTask(async () => {
    const agreement = this.agreementPendingDelete;
    if (!agreement) return;
    try {
      await agreement.destroyRecord();
      this.flashMessages.showAndHideFlash('success', 'Legal document deleted.');
    } catch {
      this.flashMessages.showAndHideFlash('error', 'Something went wrong. Please try again.');
    } finally {
      this.agreementPendingDelete = null;
    }
  });
}
